import { defineStore } from 'pinia';
import { PortalType, type School, type UserType } from '@/types/composition-functions';
import {
  buildSchoolPortalMap,
  hasGuardianAccess,
  isDualPortalUser,
  isGuardianOnlyUser,
  resolveAvailablePortals,
  type PortalUser,
  type SchoolPortalMap,
} from '@/utils/portalAccess';
import { LocalStorageKeys } from '@/constants/LocalStorageKey.constant';
import { QUERY_ACTIVE_SETTING } from '@/graphql';
import { useSessionStore } from './sessionStore';
import { useCBTSessionStore } from './cbtSession';
import { useStudentOverviewStore } from './studentOverviewStore';
import { useBranding } from '@/composables/useBranding';
import { useAuthApi } from '@/composables/useAuthApi';
import { isCbtOnlyMode } from '@/utils/studentPortalMode';

export const useUserStore = defineStore('user', () => {
  const currentUser = ref<UserType | null>(null);
  const currentSchool = ref<School | null>(null);
  const schoolType = ref<string>((sessionStorage.getItem('school_type') || '').toLowerCase());
  const isLoggedIn = ref(!!localStorage.getItem(LocalStorageKeys.token));
  const userSchools = ref<School[]>([]);
  const sessionStore = useSessionStore();
  const { updateBranding } = useBranding();
  const { fetchCurrentUser, fetchCurrentSchool, clearApolloStore } = useAuthApi();

  // Active setting — lazy query created once at setup time, loaded on demand
  const { result: activeSettingResult, load: loadActiveSetting } = QUERY_ACTIVE_SETTING();
  const schoolActiveSettings = computed(() => activeSettingResult.value);

  // Temporary Wizard State
  const tempSchoolId = ref<string>('');
  const tempSessionId = ref<string>('');
  const tempSessionStructureId = ref<string>('');
  const tempTermId = ref<string>('');
  const tempSectionId = ref<string>('');
  const tempSchoolType = ref<string>('');

  const isTertiary = computed(() => schoolType.value?.toLowerCase() === 'tertiary' || currentSchool.value?.school_type?.toLowerCase() === 'tertiary');

  // Login-time portals per school. In memory only: after a reload the
  // me-query's availablePortals (authoritative) takes over.
  const loginSchoolPortals = ref<SchoolPortalMap>({});

  const availablePortals = computed<PortalType[]>(() =>
    resolveAvailablePortals(currentUser.value as PortalUser | null, currentSchool.value?.id, loginSchoolPortals.value),
  );

  const hasStaffPortal = computed(() => availablePortals.value.includes(PortalType.Staff));
  const hasGuardianPortal = computed(() => availablePortals.value.includes(PortalType.Guardian));
  const isDualPortal = computed(() => isDualPortalUser(availablePortals.value));
  const isGuardian = computed(() => hasGuardianAccess(currentUser.value as PortalUser | null, availablePortals.value));
  const isGuardianOnly = computed(() => isGuardianOnlyUser(currentUser.value as PortalUser | null, availablePortals.value));

  // Per-school student portal surface (#326). Persisted like school_type so a
  // student's CBT-only state survives reloads before the me-query resolves.
  const studentPortalMode = ref<string>(sessionStorage.getItem('student_portal_mode') || '');
  const isCbtOnlyStudentPortal = computed(
    () => isCbtOnlyMode(studentPortalMode.value) || isCbtOnlyMode(currentSchool.value?.student_portal_mode),
  );

  function persistStudentPortalMode(mode: string | null | undefined) {
    if (!mode) return;
    studentPortalMode.value = mode;
    sessionStorage.setItem('student_portal_mode', mode);
  }

  async function getCurrentUser(): Promise<UserType | null> {
    let me: UserType | null;
    try {
      me = await fetchCurrentUser(isTertiary.value);
    } catch {
      currentUser.value = null;
      isLoggedIn.value = false;
      return null;
    }

    if (!me) {
      currentUser.value = null;
      isLoggedIn.value = false;
      return null;
    }

    currentUser.value = me;
    isLoggedIn.value = true;

    if (me.__typename === 'User' && me.schools?.length) {
      userSchools.value = me.schools;
      const savedSchoolId = localStorage.getItem(LocalStorageKeys.currentSchoolId);
      if (savedSchoolId && !currentSchool.value) {
        const matched = me.schools.find((s: School) => String(s.id) === String(savedSchoolId));
        if (matched) {
          currentSchool.value = matched;
          schoolType.value = matched.school_type || '';
          sessionStorage.setItem('school_type', schoolType.value);
          persistStudentPortalMode(matched.student_portal_mode);
        }
      } else if (currentSchool.value) {
        const refreshed = me.schools.find((s: School) => String(s.id) === String(currentSchool.value!.id));
        if (refreshed) {
          currentSchool.value = refreshed;
          schoolType.value = refreshed.school_type || '';
          sessionStorage.setItem('school_type', schoolType.value);
          persistStudentPortalMode(refreshed.student_portal_mode);
        }
      }
    }

    // me.schools lists staff memberships only, so a guardian-only account
    // (or a staff member visiting a school where they are only a guardian)
    // gets no match there; restore the saved school from the backend instead
    // of leaving the portal without a school name, type or branding.
    const meType = (me as { __typename?: string }).__typename;
    if (meType === 'User' && !currentSchool.value && localStorage.getItem(LocalStorageKeys.currentSchoolId)) {
      await restoreSavedSchool();
    }

    const meStudent = me as { __typename?: string; school?: { student_portal_mode?: string | null } | null };
    if (meStudent.__typename === 'Student') {
      persistStudentPortalMode(meStudent.school?.student_portal_mode);
    }

    if (me.__typename === 'Student' && me.currentClassroomSection?.school?.settings?.branding) {
      const school = me.currentClassroomSection.school;
      const branding = school.settings.branding;
      updateBranding({
        logo: school.logo || null,
        app_name: branding.app_name || null,
        primary_color: branding.primary_color || null,
        accent_color: branding.accent_color || null,
        favicon_url: branding.favicon_url || null,
        login_bg_url: branding.login_bg_url || null,
        hide_gradeverse_branding: branding.hide_gradeverse_branding || false,
      });
    }

    return me;
  }

  async function restoreSavedSchool() {
    const savedSchoolId = localStorage.getItem(LocalStorageKeys.currentSchoolId);
    try {
      const school = await fetchCurrentSchool();
      if (!school || String(school.id) !== String(savedSchoolId)) return;
      currentSchool.value = school;
      schoolType.value = school.school_type || '';
      sessionStorage.setItem('school_type', schoolType.value);
    } catch (error) {
      console.error('Could not restore the current school', error);
    }
  }

  const login = async(userData: UserType, token: string) => {
    currentUser.value = userData;
    isLoggedIn.value = true;
    localStorage.setItem(LocalStorageKeys.token, token);

    if ('school' in userData && userData.school?.school_type) {
      schoolType.value = userData.school.school_type || '';
      sessionStorage.setItem('school_type', schoolType.value);
    }

    if ('school' in userData) {
      persistStudentPortalMode(userData.school?.student_portal_mode);
    }
  };

  function setUserSchools(schools: School[]) {
    userSchools.value = schools;
  }

  function setLoginSchoolPortals(loginSchools: Parameters<typeof buildSchoolPortalMap>[0]) {
    loginSchoolPortals.value = buildSchoolPortalMap(loginSchools);
  }

  /** Portals in a given school: the login map, or the me-query's for the current school. */
  function getPortalsForSchool(schoolId: string): PortalType[] {
    const fromLogin = loginSchoolPortals.value[String(schoolId)];
    if (fromLogin) return fromLogin;
    if (currentSchool.value && String(currentSchool.value.id) === String(schoolId)) {
      return availablePortals.value;
    }
    return [];
  }

  function selectSchool(school: School) {
    currentSchool.value = school;
    localStorage.setItem(LocalStorageKeys.currentSchoolId, school.id ?? '');
    schoolType.value = school.school_type || '';
    sessionStorage.setItem('school_type', schoolType.value);
    persistStudentPortalMode(school.student_portal_mode);
    getCurrentUser();
  }

  async function logout() {
    currentUser.value = null;
    isLoggedIn.value = false;

    await clearApolloStore();

    sessionStore.$reset();
    useCBTSessionStore().clearSession();
    useStudentOverviewStore().reset();

    tempSchoolId.value = '';
    tempSessionId.value = '';
    tempSessionStructureId.value = '';
    tempTermId.value = '';
    tempSectionId.value = '';
    tempSchoolType.value = '';
    schoolType.value = '';
    studentPortalMode.value = '';
    userSchools.value = [];
    loginSchoolPortals.value = {};

    localStorage.clear();
    sessionStorage.clear();
  }

  async function getActiveSettngs() {
    loadActiveSetting();
  }

  return {
    currentUser,
    currentSchool,
    isLoggedIn,
    schoolActiveSettings,
    userSchools,
    login,
    logout,
    getActiveSettngs,
    getCurrentUser,
    setUserSchools,
    selectSchool,
    tempSchoolId,
    tempSessionId,
    tempSessionStructureId,
    tempTermId,
    tempSectionId,
    tempSchoolType,
    schoolType,
    isTertiary,
    studentPortalMode,
    isCbtOnlyStudentPortal,
    isGuardian,
    isGuardianOnly,
    availablePortals,
    hasStaffPortal,
    hasGuardianPortal,
    isDualPortal,
    loginSchoolPortals,
    setLoginSchoolPortals,
    getPortalsForSchool,
  };
});
